import { Injectable, Renderer2, RendererFactory2, inject } from '@angular/core';
import { Filters, Project, ProjectWithLabTest, WellProjectsList, ComponentItem, FractionComponent, DisplayItem, CountryModel, WellModel, StatusModel, ProjectStatusUpdate } from '../models/project';
import { LabTestArrayItem, LabTestGroup, LabTestRepeat, LabTestWithSampleDetails } from '../models/labTestRepeat';
import { projectListConstants } from '../common/texts-constants';
import { NUMBERS_CONSTANTS } from '../common/numbers-constants';
import { PVT_CONSTANTS, reportConstants, STATUS_CONSTANTS } from '../common/constants';
import { Chart, ChartDataset, TooltipItem } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { ReportResult, SummaryOfBPTable } from '../models/lab-test';
import { DeContaminationChart } from '../liveoil-decontamination/models/decontamination';


@Injectable({
    providedIn: 'root'
})
export class ProjectsListService {

    private renderer: Renderer2 = inject(RendererFactory2).createRenderer(null, null);

    private norm = (v: unknown) => (v ?? '').toString().trim().toLowerCase();

    private splitTokens(val: string): string[] {
        return val.trim().toLowerCase().split(/\s+/).filter(Boolean);
    }

    private includes(hay: unknown, needle: string): boolean {
        return (hay ?? '').toString().toLowerCase().includes(needle);
    }

    // Match project fields against one token
    ProjectMatchesToken(p: Project, tok: string): boolean {
        return (
            this.includes(p.projectNumber, tok) ||
            this.includes(p.projectName, tok) ||
            this.includes(p.buContact, tok) ||
            this.includes(p.leadEngineer, tok) ||
            this.includes(p.createdBy, tok) ||
            this.includes(p.status, tok)
        );
    }

    // Match well fields against one token
    WellMatchesToken(w: WellProjectsList, tok: string): boolean {
        return (
            this.includes(w.wellName, tok) ||
            this.includes(w.location, tok) ||
            this.includes(w.status, tok) ||
            this.includes(w.projectCount, tok)
        );
    }

    // Match well fields (version used in reactive filter pipeline)
    WellMatchesToken1(well: WellProjectsList, tok: string): boolean {
        return [
            well.wellName,
            well.location,
            (well.projects?.length ?? NUMBERS_CONSTANTS.zero).toString()
        ].some(f => this.norm(f).includes(tok.toLowerCase()));
    }

    // Match project fields (version used in reactive filter pipeline)
    ProjectMatchesToken1(p: ProjectWithLabTest, tok: string): boolean {
        return [
            p.projectNumber,
            p.projectName,
            p.buContact,
            p.leadEngineer,
            p.createdBy,
            p.status
        ].some(f => this.norm(f).includes(tok.toLowerCase()));
    }

    // Check if project falls within date range
    ProjectInDateRange(p: ProjectWithLabTest, start: Date | null, end: Date | null): boolean {
        if (!p.createdDateTime) return false;

        const created = new Date(p.createdDateTime);
        created.setHours(NUMBERS_CONSTANTS.zero, NUMBERS_CONSTANTS.zero, NUMBERS_CONSTANTS.zero, NUMBERS_CONSTANTS.zero);

        if (start) {
            const startDate = new Date(start);
            startDate.setHours(NUMBERS_CONSTANTS.zero, NUMBERS_CONSTANTS.zero, NUMBERS_CONSTANTS.zero, NUMBERS_CONSTANTS.zero);
            if (created < startDate) return false;
        }
        if (end) {
            const endDate = new Date(end);
            endDate.setHours(NUMBERS_CONSTANTS.zero, NUMBERS_CONSTANTS.zero, NUMBERS_CONSTANTS.zero, NUMBERS_CONSTANTS.zero);
            if (created > endDate) return false;
        }
        return true;
    }

    // Check if status matches the selected filter
    StatusMatches(pStatus: string, selected: string): boolean {
        if (!selected || selected === projectListConstants.al) return true;
        return this.norm(pStatus) === this.norm(selected);
    }

    // Determine if a well should be shown based on all filters
    ShowWell(well: WellProjectsList, filters: Filters): boolean {
        const ts = this.splitTokens(filters.search.toLowerCase());
        const countryOk = filters.country === projectListConstants.al || this.norm(well.location) === this.norm(filters.country);
        const wellOk = filters.well === projectListConstants.al || this.norm(well.wellName) === this.norm(filters.well);

        if (!countryOk || !wellOk) return false;

        const hasDate = !!(filters.dateRange.start || filters.dateRange.end);

        if (!ts.length && !hasDate) return true;

        const wellSearchOk = ts.every((t: string) => this.WellMatchesToken1(well, t));
        if (wellSearchOk && (filters.status === projectListConstants.al)) return true;

        const projs = well.projects ?? [];
        return projs.some(p => {
            const searchOk = ts.length ? ts.every((t: string) => this.ProjectMatchesToken1(p, t)) : true;
            const dateOk = hasDate ? this.ProjectInDateRange(p, filters.dateRange.start, filters.dateRange.end) : true;
            const statusOk = this.StatusMatches(p.status, filters.status);
            return searchOk && dateOk && statusOk;
        });
    }

    // Filter projects within a well based on all filters
    FilterProjectsForWell(projects: ProjectWithLabTest[], filters: Filters): ProjectWithLabTest[] {
        const ts = this.splitTokens(filters.search.toLowerCase());
        const hasDate = !!(filters.dateRange.start || filters.dateRange.end);

        return (projects ?? []).filter(p => {
            const searchOk = ts.length ? ts.every((t: string) => this.ProjectMatchesToken1(p, t)) : true;
            const dateOk = hasDate ? this.ProjectInDateRange(p, filters.dateRange.start, filters.dateRange.end) : true;
            const statusOk = this.StatusMatches(p.status, filters.status);
            return searchOk && dateOk && statusOk;
        });
    }

    // Inner table predicate: AND across tokens
    ProjectFilterPredicate(row: Project, filter: string): boolean {
        const tokens = this.splitTokens(filter);
        if (!tokens.length) return true;
        return tokens.every(tok => this.ProjectMatchesToken(row, tok));
    }

    // Outer table predicate: well OR any project must satisfy all tokens
    OuterFilterPredicate(well: WellProjectsList, filter: string, startDate: Date | null, endDate: Date | null): boolean {
        const tokens = this.splitTokens(filter);

        if (!tokens.length && !startDate && !endDate) return true;
        let dateMatch = true;
        if (startDate && endDate) {
            dateMatch = (well.projects ?? []).some((p: Project) => {
                if (!p.createdDateTime) return false;
                const created = new Date(p.createdDateTime);
                return created >= startDate && created <= endDate;
            });
        }

        const tokenMatch = tokens.every(tok =>
            this.WellMatchesToken(well, tok) ||
            (well.projects ?? []).some((p: Project) => this.ProjectMatchesToken(p, tok))
        );

        return dateMatch && tokenMatch;
    }

    // --- Chart data computation methods ---

    SortWellsByLatestProjectDate(wells: WellProjectsList[]): WellProjectsList[] {
        return wells.sort((a, b) => {
            const ad = a.projects?.[NUMBERS_CONSTANTS.zero]?.createdDateTime
                ? new Date(a.projects[NUMBERS_CONSTANTS.zero].createdDateTime).getTime()
                : NUMBERS_CONSTANTS.zero;
            const bd = b.projects?.[NUMBERS_CONSTANTS.zero]?.createdDateTime
                ? new Date(b.projects[NUMBERS_CONSTANTS.zero].createdDateTime).getTime()
                : NUMBERS_CONSTANTS.zero;
            return bd - ad;
        });
    }

    BuildCountriesLookup(countries: CountryModel[]): CountryModel[] {
        countries.sort((a, b) => a.countryName.localeCompare(b.countryName));
        return [{ countryId: null, countryName: projectListConstants.al }, ...countries];
    }

    BuildWellsLookup(wells: WellModel[]): WellModel[] {
        wells.sort((a, b) => a.wellName.localeCompare(b.wellName));
        return [{ wellId: null, wellName: projectListConstants.al, countryId: null }, ...wells];
    }

    BuildStatusLookup(statuses: StatusModel[]): StatusModel[] {
        const allowed = new Set([
            STATUS_CONSTANTS.Ready.toLowerCase(),
            STATUS_CONSTANTS.InProgress.toLowerCase(),
            STATUS_CONSTANTS.Completed.toLowerCase(),
        ]);

        const filtered = (statuses ?? [])
            .filter((s: StatusModel) => allowed.has((s?.statusName ?? '').trim().toLowerCase()));

        return [{ statusId: null, statusName: projectListConstants.al }, ...filtered];
    }

    BuildDefaultWellsLookup(): WellModel[] {
        return [{ wellId: null, wellName: projectListConstants.al, countryId: null }];
    }

    GetAcceptedCompletedExperiments(data: LabTestWithSampleDetails[]): LabTestWithSampleDetails[] {
        return data.filter(item =>
            item.experimentStatusName === STATUS_CONSTANTS.Accepted &&
            item.sampleStatusName === STATUS_CONSTANTS.Completed
        );
    }

    BuildLabTestArray(element: LabTestWithSampleDetails, repeats: LabTestRepeat[]): LabTestArrayItem[] {
        return repeats.map(repeat => ({
            wellName: element.wellName,
            cylinder: element.cylinder,
            experimentName: repeat.experimentName,
            experimentSequenceNo: repeat.experimentSequenceNo!,
            labTestRepeatId: repeat.labTestRepeatId,
            assignedToId: repeat.assignedToId!,
            experimentFullName: repeat.experimentFullName!,
            sampleIdentifier: repeat.sampleIdentifier!,
            labTestId: repeat.labTestId
        }));
    }

    FindExperimentName(labTests: LabTestArrayItem[], labRepeatId: number): string | undefined {
        return labTests.find(item => item.labTestRepeatId === labRepeatId)?.experimentName;
    }

    BuildDecontaminationChartData(
        contaminatedFluid: { x: number; y: number }[],
        contaminant: { x: number; y: number }[],
        wholeFluid: { x: number; y: number }[],
        decontaminated: { x: number; y: number }[],
        liveOil: { x: number; y: number }[]
    ): DeContaminationChart[] {
        return contaminatedFluid.map((fluidData, index) => ({
            contaminatedFluid_X: fluidData.x,
            contaminatedFluid_Y: fluidData.y,
            contaminant_X: contaminant[index].x,
            contaminant_Y: contaminant[index].y,
            deContaminated_X: wholeFluid[index].x,
            deContaminated_Y: wholeFluid[index].y,
            deContaminatedData_X: decontaminated[index].x,
            deContaminatedData_Y: decontaminated[index].y,
            liveOilDeContaminatedData_X: liveOil[index].x,
            liveOilDeContaminatedData_Y: liveOil[index].y
        }));
    }

    CanCompleteProject(data: ProjectWithLabTest): boolean {
        const findIncomplete = data.labTestListData
            .filter((x: LabTestWithSampleDetails) => x.sampleStatusName !== PVT_CONSTANTS.Completed);
        return findIncomplete.length === NUMBERS_CONSTANTS.zero;
    }

    BuildCompleteProjectStatusUpdate(projectId: number, userId: string): ProjectStatusUpdate {
        return {
            projectId,
            projectStatusId: NUMBERS_CONSTANTS.seven,
            requestStatus: PVT_CONSTANTS.Completed,
            userId,
        };
    }

    BuildUserReportQuery(
        data: Filters,
        toLocalDateStringOrNull: (date: Date | null | undefined) => string | null,
        parseToUtcDate: (date: string | null) => Date | null,
        normalizeString: (value: string | null | undefined, fallback: string) => string | null
    ): { fromDate: Date | null; toDate: Date | null; wellName: string | null; country: string | null; status: string | null } {
        const fromDate = parseToUtcDate(toLocalDateStringOrNull(data?.dateRange?.start));
        const toDate = parseToUtcDate(toLocalDateStringOrNull(data?.dateRange?.end));
        const wellName = normalizeString(data?.well, projectListConstants.all1);
        const country = normalizeString(data?.country, projectListConstants.all1);
        const status = normalizeString(data?.status, projectListConstants.all1);

        return { fromDate, toDate, wellName, country, status };
    }

    IsUserReportDateRangeValid(
        fromDate: Date | null,
        toDate: Date | null,
        diffDays: (fromDate: Date, toDate: Date, inclusive: boolean) => number | null,
        maxDays = NUMBERS_CONSTANTS.thirtyOne
    ): boolean {
        if (!fromDate || !toDate) {
            return true;
        }

        const diff = diffDays(fromDate, toDate, true) ?? NUMBERS_CONSTANTS.zero;
        return diff <= maxDays;
    }

    ComputeStatusCounts(projects: ProjectWithLabTest[]): { completed: number; inprogress: number; ready: number } {
        const counts = { completed: NUMBERS_CONSTANTS.zero, inprogress: NUMBERS_CONSTANTS.zero, ready: NUMBERS_CONSTANTS.zero };
        for (const p of projects) {
            const s = this.norm(p.status);
            if (s === STATUS_CONSTANTS.Completed.toLowerCase()) counts.completed++;
            else if (s === STATUS_CONSTANTS.InProgress.toLowerCase()) counts.inprogress++;
            else if (s === STATUS_CONSTANTS.Ready.toLowerCase()) counts.ready++;
        }
        return counts;
    }

    ComputeWellCounts(wellsList: WellProjectsList[]): { name: string | null; count: number }[] {
        return wellsList
            .map(w => ({ name: w.wellName, count: (w.projects?.length ?? NUMBERS_CONSTANTS.zero) }))
            .filter(w => w.count > NUMBERS_CONSTANTS.zero);
    }

    // --- Report data transformation methods ---

    FilterLabTestDataList(response: LabTestWithSampleDetails[]): LabTestWithSampleDetails[] {
        const labtest: Record<string, LabTestWithSampleDetails> = {};
        let labListObj: LabTestWithSampleDetails = {} as LabTestWithSampleDetails;
        response.forEach((data: LabTestWithSampleDetails, i: number) => {
            labListObj = {
                index: i + NUMBERS_CONSTANTS.one,
                labTestId: data.labTestId,
                wellId: data.wellId,
                wellName: data.wellName,
                reservoirTemperature: data.reservoirTemperature,
                reservoirPressure: data.reservoirPressure,
                cylinder: data.cylinder,
                statusName: data.sampleStatusName ?? '',
                labTestRepeat: [],
                createdDateTime: data.createdDateTime,
                projectId: data.projectId,
                reservoirName: data.reservoirName,
                samplingType: data.samplingType,
                sampleType: data.sampleType,
                samplingDate: data.samplingDate,
                samplingTypeName: data.samplingTypeName,
                sampleCreatedBy: data.sampleCreatedBy,
                sampleIdentifier: data.sampleIdentifier,
                reportSampletype: data.reportSampletype
            };
            if (!labtest[data.labTestId]) {
                labtest[data.labTestId] = labListObj;
            }
            labtest[`${data.labTestId}`].labTestRepeat.push({
                experimentId: data.experimentId ?? '',
                experimentName: data.experimentName ?? '',
                experimentFullName: data.experimentFullName,
                experimentSequenceNo: data.experimentSequenceNo,
                labTestRepeatId: data.labTestRepeatId!,
                assignedTo: data.assignedTo ?? '',
                statusId: data.experimentStatusId,
                statusName: data.experimentStatusName ?? '',
                assignedToId: data.assignedToId,
                action: 'IN PROGRESS',
                notes: data.experimentNotes?.toString(),
                labTestId: data.labTestId,
                sampleIdentifier: data.sampleIdentifier,
                experimentCreatedBy: data.sampleCreatedBy,
                createdDateTime: data.createdDateTime
            });
        });
        return Object.values(labtest);
    }

    GroupByLabTestId(data: LabTestRepeat[]): LabTestGroup[] {
        const bucket = data.reduce((acc, item) => {
            const key = item.labTestId;
            (acc[key] ??= []).push(item);
            return acc;
        }, {} as Record<number, LabTestRepeat[]>);

        return Object.entries(bucket)
            .map(([labTestIdStr, items]) => ({
                labTestId: Number(labTestIdStr),
                items,
                count: items.length,
            }))
            .sort((a, b) => a.labTestId - b.labTestId);
    }

    AttachSummarySampleIdentifiers(summaryRows: SummaryOfBPTable[], labTests: LabTestArrayItem[]): SummaryOfBPTable[] {
        summaryRows.forEach(row => {
            const match = labTests.find(d => d.labTestRepeatId === row.labRepeatId);
            if (match) {
                row.sampleIdentifier = match.sampleIdentifier;
            }
        });
        return summaryRows;
    }

    GroupSummaryBySampleIdentifier(summaryRows: SummaryOfBPTable[]): Record<string, SummaryOfBPTable[]> {
        return (summaryRows ?? []).reduce((acc: Record<string, SummaryOfBPTable[]>, item) => {
            const key = item?.sampleIdentifier?.toLowerCase() ?? '';
            (acc[key] ||= []).push(item);
            return acc;
        }, {});
    }

    SortSummaryByTemperatureDesc(rows: SummaryOfBPTable[]): SummaryOfBPTable[] {
        return rows.sort((a, b) => b.temp - a.temp);
    }

    AttachExperimentSampleIdentifiers(expResults: ReportResult[], labTests: LabTestArrayItem[]): ReportResult[] {
        expResults.forEach((result) => {
            if (!('labRepeatId' in result)) {
                return;
            }

            const match = labTests.find(d => d.labTestRepeatId === result.labRepeatId);
            if (!match) {
                return;
            }

            result.sampleIdentifier = match.sampleIdentifier;
        });
        return expResults;
    }

    SortExpResultsBySequence(expResults: ReportResult[], labTests: LabTestArrayItem[]): ReportResult[] {
        return expResults.sort((a, b) => {
            const seqA = 'labTestRepeatId' in a
                ? labTests.find(item => item.labTestRepeatId === a.labTestRepeatId)?.experimentSequenceNo
                : undefined;
            const seqB = 'labTestRepeatId' in b
                ? labTests.find(item => item.labTestRepeatId === b.labTestRepeatId)?.experimentSequenceNo
                : undefined;
            return (seqA ?? NUMBERS_CONSTANTS.zero) - (seqB ?? NUMBERS_CONSTANTS.zero);
        });
    }

    TransformComponents(components: ComponentItem[]): FractionComponent[] {
        const starMapping: Record<string, string> = projectListConstants.starMapping;
        return components
            .map((val): FractionComponent => {
                if (val.component === projectListConstants.c36OverC36Plus) {
                    val.component = projectListConstants.c36;
                }
                if (val.componentName === projectListConstants.hexatriacontanesPlus) {
                    val.componentName = projectListConstants.hexatriacontanes;
                }

                val.mw = Number(val.mw).toFixed(NUMBERS_CONSTANTS.one);
                val.density = Number(val.density).toFixed(NUMBERS_CONSTANTS.four);
                val.StarsCol = starMapping[val.component] || "";

                return val;
            })
            .filter((val) => val.component !== projectListConstants.c50Plus);
    }

    DestroyCharts(charts: unknown[]): void {
        charts.forEach((chart) => {
            if (chart && typeof (chart as Chart).destroy === 'function') {
                (chart as Chart).destroy();
            }
        });
    }

    BuildMaterialBalancePlotData<T extends { compound: string; xz: number; yz: number }>(
        rows: T[]
    ): { x: number; y: number; compound: string }[] {
        return rows
            .filter(x => x.compound.toLowerCase() !== projectListConstants.c11 && x.compound.toLowerCase() !== projectListConstants.c12)
            .map((x) => ({
                x: Number(x.xz.toFixed(NUMBERS_CONSTANTS.four)),
                y: Number(x.yz.toFixed(NUMBERS_CONSTANTS.four)),
                compound: x.compound,
            }));
    }

    BuildHoffmanPlotData<T extends { compound: string; f: number; k: number }>(
        rows: T[]
    ): {
        primary: { x: number; y: number; compound: string }[];
        lightHydrocarbonOnly: { x: number; y: number; compound: string }[];
    } {
        const primary = rows
            .filter(x => x.compound.toLowerCase() !== projectListConstants.c11 && x.compound.toLowerCase() !== projectListConstants.c12)
            .map((x) => ({
                x: x.f,
                y: x.k,
                compound: x.compound,
            }));

        const lightHydrocarbonOnly = rows
            .map((x) => ({
                x: x.f,
                y: x.k,
                compound: x.compound,
            }))
            .filter(data => (projectListConstants.lightHydrocarbons as readonly string[]).includes(data.compound));

        return { primary, lightHydrocarbonOnly };
    }


    CanvasToHighResDataURL(
        canvas: HTMLCanvasElement,
        scale: number,
        mime: string,// = projectListConstants.imageOrPng,
        quality:number// = NUMBERS_CONSTANTS.onePointZero
    ): string {
        scale = NUMBERS_CONSTANTS.one;
        const hiCanvas = this.renderer.createElement('canvas') as HTMLCanvasElement;
        hiCanvas.width = canvas.width * scale;
        hiCanvas.height = canvas.height * scale;

        const hiCtx = hiCanvas.getContext('2d')!;
        hiCtx.imageSmoothingEnabled = true;
        hiCtx.imageSmoothingQuality = 'high';

        hiCtx.setTransform(scale, NUMBERS_CONSTANTS.zero, NUMBERS_CONSTANTS.zero, scale, NUMBERS_CONSTANTS.zero, NUMBERS_CONSTANTS.zero);
        hiCtx.fillStyle = '#FFFFFF';
        hiCtx.fillRect(0, 0, hiCanvas.width, hiCanvas.height);
        hiCtx.drawImage(canvas, NUMBERS_CONSTANTS.zero, NUMBERS_CONSTANTS.zero);

        return hiCanvas.toDataURL(mime, quality);
    }

    WellPalette(count: number): string[] {
        const base = [...projectListConstants.wellPaletteBase];
        if (count <= base.length) return base.slice(NUMBERS_CONSTANTS.zero, count);
        return Array.from({ length: count }, (_, i) => `hsl(${Math.round((NUMBERS_CONSTANTS.threeHundredSixty / count) * i)}, ${NUMBERS_CONSTANTS.sixtyFive}%, ${NUMBERS_CONSTANTS.fiftyFive}%)`);
    }

    BuildStatusPieChart(
        ctx: CanvasRenderingContext2D,
        counts: { ready: number; inprogress: number; completed: number },
        statusColors: { ready: string; inprogress: string; completed: string },
        sumFn: (arr: number[]) => number
    ): Chart {
        Chart.register(ChartDataLabels);
        const labels = [
            `${STATUS_CONSTANTS.Ready} ${counts.ready}`,
            `${STATUS_CONSTANTS.InProgress} ${counts.inprogress}`,
            `${STATUS_CONSTANTS.Completed} ${counts.completed}`
        ];
        const data = [counts.ready, counts.inprogress, counts.completed];
        const isSingleSlice = data.filter(v => v > NUMBERS_CONSTANTS.zero).length === NUMBERS_CONSTANTS.one;

        return new Chart(ctx, {
            type: projectListConstants.doughnut,
            data: {
                labels,
                datasets: [{
                    data,
                    backgroundColor: [
                        statusColors.ready,
                        statusColors.inprogress,
                        statusColors.completed
                    ],
                    borderWidth: NUMBERS_CONSTANTS.zero,
                    spacing: isSingleSlice ? NUMBERS_CONSTANTS.zero : NUMBERS_CONSTANTS.one,
                    borderRadius: NUMBERS_CONSTANTS.zero,
                    hoverOffset: NUMBERS_CONSTANTS.six
                }]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                cutout: projectListConstants.fortyPercent,
                radius: projectListConstants.ninetyPercent,
                plugins: {
                    legend: {
                        position: reportConstants.right,
                        align: reportConstants.center,
                        labels: {
                            usePointStyle: true,
                            pointStyle: projectListConstants.circle,
                            boxWidth: NUMBERS_CONSTANTS.six, boxHeight: NUMBERS_CONSTANTS.six, padding: NUMBERS_CONSTANTS.twelve,
                            font: { family: projectListConstants.monospace, size: NUMBERS_CONSTANTS.twelve },
                            generateLabels: (chart: Chart) => {
                                const ds = chart.data.datasets[NUMBERS_CONSTANTS.zero] as ChartDataset<'pie', number[]>;
                                const statuses = [STATUS_CONSTANTS.Ready, STATUS_CONSTANTS.InProgress, STATUS_CONSTANTS.Completed];
                                const FIG = projectListConstants.fig;
                                const maxStatusLen = statuses.reduce((m, s) => Math.max(m, s.length), NUMBERS_CONSTANTS.zero as number);
                                const pad = (s: string) => s + FIG.repeat(Math.max(NUMBERS_CONSTANTS.zero, maxStatusLen - s.length));
                                const bgColors = ds.backgroundColor;
                                return statuses.map((s, i) => {
                                    const color = Array.isArray(bgColors) ? bgColors[i] : bgColors;
                                    return {
                                        text: `${pad(s)}   ${ds.data[i]}`,
                                        fillStyle: color,
                                        strokeStyle: color,
                                        hidden: false,
                                        index: i
                                    };
                                });
                            }
                        }
                    },
                    tooltip: {
                        callbacks: {
                            title: () => '',
                            label: (item: TooltipItem<'pie'>) => {
                                const rawLabel = item.label ?? '';
                                const statusOnly = rawLabel.replace(/\s+\d+$/, '');
                                const v = item.raw as number ?? NUMBERS_CONSTANTS.zero;
                                const total = sumFn(item.dataset.data as number[]);
                                const pct = total ? ((v * NUMBERS_CONSTANTS.hundred) / total).toFixed(NUMBERS_CONSTANTS.one) : projectListConstants.zeroPointZero;
                                return `${statusOnly}: ${v} (${pct}%)`;
                            }
                        }
                    },
                    datalabels: {
                        display: false
                    }
                },
                layout: { padding: { right: NUMBERS_CONSTANTS.eight } }
            }
        });
    }

    BuildWellPieChart(
        ctx: CanvasRenderingContext2D,
        wells: { name: string | null; count: number }[],
        sumFn: (arr: number[]) => number
    ): { chart: Chart; legendData: DisplayItem[] } {
        Chart.register(ChartDataLabels);
        const labels = wells.map(w => w.name);
        const data = wells.map(w => w.count);
        const colors = this.WellPalette(wells.length);
        const isSingleSlice = data.filter(v => v > NUMBERS_CONSTANTS.zero).length === NUMBERS_CONSTANTS.one;

        let legendData: DisplayItem[] = [];

        const chart = new Chart(ctx, {
            type: projectListConstants.doughnut,
            data: {
                labels,
                datasets: [{
                    data,
                    backgroundColor: colors,
                    borderWidth: NUMBERS_CONSTANTS.zero,
                    spacing: isSingleSlice ? NUMBERS_CONSTANTS.zero : NUMBERS_CONSTANTS.one,
                    borderRadius: NUMBERS_CONSTANTS.zero,
                    hoverOffset: NUMBERS_CONSTANTS.six
                }]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                cutout: projectListConstants.fortyPercent,
                radius: projectListConstants.ninetyPercent,
                plugins: {
                    legend: {
                        position: reportConstants.right,
                        align: reportConstants.center,
                        labels: {
                            usePointStyle: true,
                            pointStyle: projectListConstants.circle,
                            boxWidth: NUMBERS_CONSTANTS.six, boxHeight: NUMBERS_CONSTANTS.six, padding: NUMBERS_CONSTANTS.twelve,
                            font: { family: projectListConstants.monospace, size: NUMBERS_CONSTANTS.twelve },
                            generateLabels: (chart: Chart) => {
                                const ds = chart.data.datasets[NUMBERS_CONSTANTS.zero] as ChartDataset<'pie', number[]>;
                                const names: string[] = chart.data.labels as string[] ?? [];
                                const counts: number[] = ds.data ?? [];
                                const FIG = projectListConstants.fig;
                                const maxNameLen = names.reduce((m, s) => Math.max(m, s.length), NUMBERS_CONSTANTS.zero as number);
                                const padFig = (s: string) => s + FIG.repeat(Math.max(NUMBERS_CONSTANTS.zero, maxNameLen - s.length));
                                const bgColors = ds.backgroundColor;

                                const allData: DisplayItem[] = names.map((name, i) => {
                                    const color = Array.isArray(bgColors) ? bgColors[i] : bgColors;
                                    return {
                                        text: `${padFig(name)}   ${counts[i]}`,
                                        fillStyle: color as string,
                                        strokeStyle: color as string,
                                        hidden: false,
                                        index: i
                                    };
                                });

                                legendData = allData;
                                return allData.slice(NUMBERS_CONSTANTS.zero, NUMBERS_CONSTANTS.ten);
                            }
                        }
                    },
                    tooltip: {
                        callbacks: {
                            title: () => '',
                            label: (item: TooltipItem<'pie'>) => {
                                const rawLabel = item.label ?? '';
                                const wellName = rawLabel.replace(/\s+\d+$/, '');
                                const v = item.raw as number ?? NUMBERS_CONSTANTS.zero;
                                const total = sumFn(item.dataset.data as number[]);
                                const pct = total ? ((v * NUMBERS_CONSTANTS.hundred) / total).toFixed(NUMBERS_CONSTANTS.one) : projectListConstants.zeroPointZero;
                                return `${wellName}: ${v} (${pct}%)`;
                            }
                        }
                    },
                    datalabels: {
                        display: false
                    }
                },
                layout: { padding: { right: NUMBERS_CONSTANTS.eight } }
            }
        });

        return { chart, legendData };
    }
}
